import { useGitFileCheck } from './useGitFileCheck';
import { useGitRefCheck } from './useGitRefCheck';
import { EffectiveGitReference } from './useResolvedGitConfig';

export type GitReferenceErrorKind = 'config' | 'repo' | 'ref' | 'file';

export interface GitReferenceError {
  kind: GitReferenceErrorKind;
  message?: string;
}

export interface GitCheckError {
  kind: 'repo' | 'ref';
  message?: string;
  rawError: unknown;
}

export interface GitReferenceStatus {
  foundRef: boolean;
  foundFile: boolean;
  isRefLoading: boolean;
  isFileLoading: boolean;
}

export interface UseGitCheckStatusParams {
  config: Pick<EffectiveGitReference, 'configFilePath' | 'sourceId' | 'ref'>;
  hasAuth: boolean;
  enableFileCheck: boolean;
  isConfigLoading: boolean;
  isConfigError: boolean;
}

export function useGitCheckStatus({
  config,
  hasAuth,
  enableFileCheck,
  isConfigLoading,
  isConfigError,
}: UseGitCheckStatusParams) {
  const refQuery = useGitRefCheck({
    config,
    hasAuth,
    isConfigLoading,
  });

  const fileQuery = useGitFileCheck({
    config,
    enabled: enableFileCheck,
    isConfigLoading,
    blockedByPriorErrors: isConfigError || !!refQuery.error,
  });

  const errorKind: GitReferenceErrorKind | undefined = isConfigError
    ? 'config'
    : (refQuery.error?.kind ?? (fileQuery.error ? 'file' : undefined));

  const explainedError = getExplainedError(
    refQuery.error?.rawError,
    fileQuery.error?.rawError
  );
  const infoMessage = explainedError || refQuery.error?.message;

  return {
    status: {
      foundRef: refQuery.found,
      foundFile: fileQuery.found,
      isRefLoading: refQuery.isLoading,
      isFileLoading: fileQuery.isLoading,
    } satisfies GitReferenceStatus,
    error: errorKind ? { kind: errorKind, message: infoMessage } : undefined,
  };
}

function getExplainedError(...errors: Array<unknown | null>) {
  if (
    errors.some(
      (e) =>
        e instanceof Error &&
        e?.message.startsWith(
          'Authentication required: Invalid username or token'
        )
    )
  ) {
    return 'The configured Git Credentials are invalid or expired. Update the credentials to restore access.';
  }
  return '';
}
